/* Built In Imports */
import { useContext } from 'react';

/* External Imports */
import { Box, Heading, Image } from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import WeaveCard from '@components/Card/WeaveCard';
import Breadcrum from '@components/Navigation/Breadcrum';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import StructuredTextContent from '@components/UI/StructuredTextContent';
import HeadComponent from '@components/Utility/HeadComponent';
import SocialIcon from '@components/Utility/SocialIcon';
import config from '@config';
import WrapLayout from '@Layout/WrapLayout';
import { AuthContext } from '@store/auth-context';
import { LangStyle } from 'constants/index';

/* Services */
import { getLocale } from '@components/Utility/Shared/SharedService';
import { nanoid } from 'nanoid';
import {
  fetchFooterData,
  fetchHeaderMenuData,
  fetchHeaderTopMenuData,
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/outreachService';

/* Styles */

/**
 *
 * @param {string} region - Current User Region
 * @param {string} language - Current User Language
 * @param {string} pageData - Health Landing Page Data
 */
const OthersThirdLevel = ({ region, language, pageData }) => {
  const authContext = useContext(AuthContext);

  return (
    <WrapLayout pad={0}>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData.availableLocales}
      >
        {' '}
      </HeadComponent>
      <Breadcrum
        page={pageData.breadcrumbs}
        alsoin={pageData.alsoin}
        url=""
        region={authContext.region}
        lang={language}
        pageConfig={pageData?.pageConfig}
      />
      <Box maxW="1300px" mx="auto">
        <SocialIcon
          url={pageData.url}
          isVisible={true}
          // isLikeBookmarkVisible={isLikeBookmarkVisible}
          isLiked={pageData.isLiked}
          isBookmarked={pageData.isBookmarked}
          // updateCards={updateCards}
        />
        <Box maxW="1270px" mx="auto">
          <Box backgroundColor="#f5f5f5" textAlign="center">
            <Heading
              as="h1"
              display="inline-block"
              width="100%"
              maxWidth="850px"
              textAlign="center"
              fontFamily={
                LangStyle[language]?.headingFont
                  ? LangStyle[language].headingFont
                  : 'FedraSerifAStdBook'
              }
              fontSize={{ base: '27px', md: '45px', lg: '45px' }}
              color="#28231e"
              margin="0"
              p={{
                base: '19px 16px',
                md: '27px 56px 16px',
                lg: '55px 0px 24px',
              }}
              fontWeight={
                LangStyle[language]?.titleFontWeight
                  ? LangStyle[language].titleFontWeight
                  : '600'
              }
            >
              {pageData.title}
            </Heading>
          </Box>
          <Box
            clipPath="polygon(0 0, 100% 0, 100% 80%, 0% 100%)"
            backgroundColor="#f5f5f5"
            minHeight={{
              base: '20px',
              sm: '130px',
              md: '130px',
              lg: '350px',
            }}
            margin="-2px auto 0"
          ></Box>
          <Box
            width={{ base: '100%', lg: '66%' }}
            padding={{ base: '0', sm: '0 15px' }}
            m={{
              base: '-20px auto 0',
              sm: '-20px auto 0',
              lg: '-110px auto 0',
              xl: '-240px auto 0',
            }}
          >
            {pageData?.heroImage.url && (
              <Box
                boxShadow="0 0 65px 7px rgb(124, 124, 124, 0.75)"
                display="inline-block"
                pos="relative"
                background="#e1e1e1"
              >
                <LazyLoadImageComponent
                  src={pageData?.heroImage.url}
                  alt={pageData?.heroImage.alt}
                  title={pageData?.heroImage.title}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          as="article"
          maxW="730px"
          mx={{ base: '15px', lg: 'auto' }}
          my="50px"
          gridGap="30px"
          fontSize="18px"
          className="event-text"
        >
          {pageData?.state?.countryImage.url && (
            <Box
              float={{ base: 'none', lg: 'left' }}
              width="50%"
              mx={{ base: 'auto', lg: '15px' }}
            >
              <LazyLoadImageComponent
                src={pageData?.state?.countryImage.url}
                alt={pageData?.state?.countryImage.alt}
                title={pageData?.state?.countryImage.title}
              />
            </Box>
          )}
          {StructuredTextContent(pageData.body, region, language)}
        </Box>
      </Box>
      {/* move it to save_the_weave_related_content component */}

      {pageData.relatedSaveTheWeavePost[0]?.cards?.length > 0 && (
        <Box maxW="1300px" mx="auto">
          <Box
            as="section"
            float="left"
            width="100%"
            background={{
              base: 'linear-gradient(to bottom, #ffffff 1%, #ccc3b0 97%)',
            }}
            bgImage={{
              lg: `url('${config.staticPath}/assets/images/ornaments-leaves.svg'), linear-gradient(to bottom, #ffffff 1%, #ccc3b0 97%)`,
            }}
            backgroundRepeat="no-repeat"
            backgroundSize={{ lg: '500px, cover', md: 'cover' }}
            backgroundPosition={{ lg: '0 80px, center', md: 'center' }}
            mt={{ md: '30px', sm: '0' }}
          >
            <Box
              float="left"
              width="100%"
              mt={{ base: '62px', md: '18px', lg: '18px' }}
            >
              <Box
                float="left"
                width="100%"
                fontFamily="'FedraSansStd-A-medium', sans-serif"
                fontSize={{ base: '24px', md: '30px', lg: '30px' }}
                lineHeight="1.13"
                textAlign="center"
                color="#02026c"
              >
                {pageData?.relatedSaveTheWeavePost[0]?.title}
              </Box>
              <Box
                float="left"
                width="250px"
                m="5px calc((100% - 250px) / 2) 0"
              >
                <Image
                  alt=""
                  title="divider"
                  src={`${config.staticPath}/assets/images/divider2.svg`}
                  float="left"
                  width="100%"
                />
              </Box>
            </Box>
            <Box maxW="1200px" mx="auto" my={3}>
              <Box
                display="flex"
                w="full"
                gridGap="30px"
                py="30px"
                flexWrap="wrap"
                justifyContent="center"
              >
                {pageData.relatedSaveTheWeavePost[0]?.cards?.map(card => {
                  return <WeaveCard key={nanoid(4)} card={card} />;
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const outreachThirdLevelPaths = config.USE_LOCAL_URL
    ? [
        `/in/outreach/save-the-weave/indian-weaves/negamam-cotton`,
        '/in/outreach/save-the-weave/indian-weaves/angara-cotton',
        `/in/outreach/save-the-weave/indian-weaves/salem-cotton`,
      ]
    : await getLandingPaths(
        'L3',
        '/outreach/save-the-weave/',
        null,
        'SaveTheWeaves'
      );
  return {
    paths: outreachThirdLevelPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, firstLevel, secondLevel, thirdLevel },
  req,
}) => {
  const language = region;
  const headerTopMenuData = await fetchHeaderTopMenuData(
    getLocale('', language),
    'isotopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale('', language),
    'iso_non_regional_menu'
  );
  const footerData = await fetchFooterData(
    getLocale('', language),
    'isofooter'
  );
  const pageData = await fetchDetails(
    '',
    language,
    `${firstLevel}/${secondLevel}/${thirdLevel}`,
    { preview, data: previewData }
  );

  return {
    props: {
      headerTopMenuData: headerTopMenuData?.data || null,
      headerMenuData: headerMenuData?.data || null,
      region: '',
      language: language,
      footerData: footerData?.data || null,
      pageData: pageData || null,
      isPreview: preview ? true : false,
    },
  };
};

export default OthersThirdLevel;
